import '@material/web/icon/icon.js'
import '@material/web/button/elevated-button.js'
import '@operato/i18n/ox-i18n.js'

import { css, html, PropertyValues, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { OxPropertyEditor, PropertySpec } from '@operato/property-editor'
import { Component } from '@hatiolab/things-scene'

import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

const EXT_SPECGLOSS = 'KHR_materials_pbrSpecularGlossiness'

class SpecGlossCompat {
  name = EXT_SPECGLOSS
  extendMaterialParams() {
    return Promise.resolve()
  }
}

function registerSpecGlossCompat(loader: GLTFLoader) {
  loader.register(() => new SpecGlossCompat())
}

@customElement('property-editor-gltf-info')
export default class GLTFInfoEditor extends OxPropertyEditor {
  static styles = [
    ...OxPropertyEditor.styles,
    css`
      div[info] {
        display: flex;
        flex-direction: row;
        font-size: 12px;
      }

      div[info] * {
        align-self: center;
      }

      div[info] title {
        font-weight: bold;
      }

      .buttons {
        display: flex;
        gap: 4px;
        margin-left: auto;
      }

      .buttons md-elevated-button {
        --md-icon-size: 24px;
      }
    `
  ]

  @property({ type: String }) declare src: string | undefined

  @state() declare width: number
  @state() declare height: number
  @state() declare depth: number

  constructor() {
    super()
    this.width = 0
    this.height = 0
    this.depth = 0
  }

  editorTemplate(value: any, spec: PropertySpec): TemplateResult {
    const valid = this.width && this.height && this.depth

    var property = spec.property || {}
    var { action } = property

    return html`
      <fieldset fullwidth>
        <legend><ox-i18n msgid="label.gltf-info">GLTF info.</ox-i18n></legend>

        <div>
          <div info>
            <div label>[W x H x D] :</div>
            ${valid
              ? html` <div value>${this.width} x ${this.height} x ${this.depth}</div>
                  <div class="buttons">
                    <md-elevated-button
                      title="원래 크기로 설정"
                      @click=${() => {
                        this._applyAction(action, {
                          width: this.width,
                          height: this.height,
                          depth: this.depth
                        })
                      }}
                    >
                      <md-icon>autorenew</md-icon>
                    </md-elevated-button>
                    <md-elevated-button
                      title="현재 크기에 비율 맞춤"
                      @click=${() => this._applyProportional(action)}
                    >
                      <md-icon>aspect_ratio</md-icon>
                    </md-elevated-button>
                  </div>`
              : html` <div></div> `}
          </div>
        </div>
      </fieldset>
    `
  }

  private _applyAction(action: any, dimension: { width: number; height: number; depth: number }) {
    this.dispatchEvent(
      new CustomEvent('i-need-selected', {
        bubbles: true,
        composed: true,
        detail: {
          callback: (selected: any[]) => {
            typeof action === 'function' && action(selected[0], dimension)
          }
        }
      })
    )
  }

  /**
   * 현재 컴포넌트의 W/H/D 중 가장 큰 값을 기준으로,
   * 원래 모델의 비율에 맞게 나머지 치수를 조절한다.
   */
  private _applyProportional(action: any) {
    this.dispatchEvent(
      new CustomEvent('i-need-selected', {
        bubbles: true,
        composed: true,
        detail: {
          callback: (selected: any[]) => {
            const component = selected[0]
            if (!component || typeof action !== 'function') return

            const { width: cw = 1, height: ch = 1, depth: cd = 1 } = component.state
            const { width: ow, height: oh, depth: od } = this // 원래 모델 치수

            if (!ow || !oh || !od) return

            // 현재 치수 중 가장 큰 값과, 그에 대응하는 원래 모델 치수로 scale 계산
            const maxCurrent = Math.max(cw, ch, cd)
            let scale: number

            if (maxCurrent === cw) {
              scale = cw / ow
            } else if (maxCurrent === ch) {
              scale = ch / oh
            } else {
              scale = cd / od
            }

            action(component, {
              width: Math.round(ow * scale),
              height: Math.round(oh * scale),
              depth: Math.round(od * scale)
            })
          }
        }
      })
    )
  }

  updated(changes: PropertyValues<this>) {
    if (changes.has('src')) {
      this.dispatchEvent(
        new CustomEvent('i-need-selected', {
          bubbles: true,
          composed: true,
          detail: {
            callback: async (selected: any[]) => {
              await this.fetchSourceInfo(selected[0], this.src!)
            }
          }
        })
      )
    }
  }

  async fetchSourceInfo(component: Component, src: string) {
    if (!src || !src.trim()) {
      return
    }

    try {
      const path = component.app.url(src)

      let gltfLoader = new GLTFLoader()
      registerSpecGlossCompat(gltfLoader)

      gltfLoader.setCrossOrigin('use-credentials')

      return new Promise((resolve: any) => {
        gltfLoader.load(path, gltf => {
          var box = new THREE.Box3().setFromObject(gltf.scene)
          var { x, y, z } = box.getSize(new THREE.Vector3())

          this.width = Math.floor(x)
          this.depth = Math.floor(y)
          this.height = Math.floor(z)

          resolve()
        })
      })
    } catch (e) {
      console.error(e)
      return
    }
  }
}
